// src/pages/Profile.jsx
import React, { useState } from 'react';
import { User, Shield, Trash2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import Sidebar from '../components/Sidebar';
import Toast from '../components/Toast';
import Modal from '../components/Modal';
import { mockUser } from '../data/mockData';

const Profile = ({ darkMode, toggleDarkMode }) => {
  const [user, setUser] = useState(mockUser);
  const [toast, setToast] = useState(null);

  // Personal info form
  const [formData, setFormData] = useState({
    name: user.name,
    email: user.email,
  });

  // Change password modal state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [pwdForm, setPwdForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [pwdErrors, setPwdErrors] = useState({});
  const [pwdLoading, setPwdLoading] = useState(false);

  const apiBase = process.env.REACT_APP_API_BASE || '';

  const setToastAuto = (message, type = 'info', timeout = 4000) => {
    setToast({ message, type });
    if (timeout) setTimeout(() => setToast(null), timeout);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSave = (e) => {
    e.preventDefault();
    setUser({ ...user, ...formData });
    setToastAuto('Profile updated successfully!', 'success');
  };

  const handleDeleteAccount = () => {
    if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      // Demo behavior: show toast. Hook your backend here.
      setToastAuto('Account deletion initiated...', 'warning');
    }
  };

  // --- Change Password modal handlers ---
  const openChangePassword = () => {
    setPwdForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setPwdErrors({});
    setShowChangePassword(true);
  };

  const validatePwdForm = () => {
    const errors = {};
    if (!pwdForm.currentPassword) errors.currentPassword = 'Enter current password';
    if (!pwdForm.newPassword) errors.newPassword = 'Enter a new password';
    else if (pwdForm.newPassword.length < 8) errors.newPassword = 'Password must be at least 8 characters';
    if (pwdForm.confirmPassword !== pwdForm.newPassword) errors.confirmPassword = "Passwords don't match";
    setPwdErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePwdChange = (e) => {
    setPwdForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const submitChangePassword = async (e) => {
    e?.preventDefault();
    setPwdErrors({});
    if (!validatePwdForm()) return;

    setPwdLoading(true);

    try {
      if (apiBase) {
        // If backend exists, call it. Expect JSON { success: true } or { error: '...' }
        const resp = await fetch(`${apiBase}/change-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            currentPassword: pwdForm.currentPassword,
            newPassword: pwdForm.newPassword
          }),
          credentials: 'include'
        });

        const text = await resp.text();
        let data = null;
        try { data = text ? JSON.parse(text) : null; } catch { /* ignore parse error */ }

        if (!resp.ok) {
          const msg = data?.error || data?.detail || text || `Server returned ${resp.status}`;
          throw new Error(msg);
        }

        // Backend success
        setShowChangePassword(false);
        setToastAuto('Password changed successfully.', 'success');
      } else {
        // No backend: simulate delay & success for demo
        await new Promise(res => setTimeout(res, 800));
        setShowChangePassword(false);
        setToastAuto('Password changed (simulated).', 'success');
      }
    } catch (err) {
      console.error('Change password error:', err);
      setPwdErrors({ form: err.message || 'Failed to change password' });
      setToastAuto(err.message || 'Failed to change password', 'error');
    } finally {
      setPwdLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <Navbar darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <div className="flex">
        <Sidebar />

        <main className="flex-1 p-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Profile Settings
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Manage your account settings and preferences
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Profile Card */}
            <div className="lg:col-span-1">
              <div className="card text-center">
                <img
                  src={user.avatar}
                  alt={user.name}
                  className="w-24 h-24 rounded-full mx-auto mb-4"
                />
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                  {user.name}
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {user.email}
                </p>
                <div className="inline-block px-4 py-1 bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 rounded-full text-sm font-medium">
                  {user.plan} Plan
                </div>

                {/* Quota Usage */}
                <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <div className="text-left">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Monthly Quota
                      </span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {user.quotaUsed}/{user.quotaTotal}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-primary-600 h-2 rounded-full"
                        style={{ width: `${(user.quotaUsed / user.quotaTotal) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Settings Forms */}
            <div className="lg:col-span-2 space-y-6">
              {/* Personal Information */}
              <div className="card">
                <div className="flex items-center space-x-2 mb-6">
                  <User className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    Personal Information
                  </h3>
                </div>
                <form onSubmit={handleSave} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="input-field"
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <button type="submit" className="btn-primary">
                      Save Changes
                    </button>

                    {/* Change Password triggers modal */}
                    <button
                      type="button"
                      onClick={openChangePassword}
                      className="btn-secondary"
                    >
                      Change Password
                    </button>
                  </div>
                </form>
              </div>

              {/* Security */}
              <div className="card">
                <div className="flex items-center space-x-2 mb-6">
                  <Shield className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    Security
                  </h3>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Password
                    </label>
                    {/* Kept as a simple button that opens the modal */}
                    <button
                      type="button"
                      onClick={openChangePassword}
                      className="btn-secondary"
                    >
                      Change Password
                    </button>
                  </div>

                  {/* two-factor auth removed per request */}
                </div>
              </div>

              {/* Danger Zone */}
              <div className="card border-2 border-red-200 dark:border-red-800">
                <div className="flex items-center space-x-2 mb-4">
                  <Trash2 className="w-5 h-5 text-red-600" />
                  <h3 className="text-xl font-bold text-red-600">
                    Delete Account
                  </h3>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Once you delete your account, there is no going back. Please be certain.
                </p>
                <button
                  onClick={handleDeleteAccount}
                  className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  Delete Account
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Change Password Modal */}
      <Modal
        isOpen={showChangePassword}
        onClose={() => setShowChangePassword(false)}
        title="Change Password"
      >
        <form onSubmit={submitChangePassword} className="space-y-4">
          {pwdErrors.form && (
            <div className="text-sm text-red-500">{pwdErrors.form}</div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Current password
            </label>
            <input
              name="currentPassword"
              type="password"
              value={pwdForm.currentPassword}
              onChange={handlePwdChange}
              className="input-field"
              autoComplete="current-password"
            />
            {pwdErrors.currentPassword && <div className="text-sm text-red-500 mt-1">{pwdErrors.currentPassword}</div>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              New password
            </label>
            <input
              name="newPassword"
              type="password"
              value={pwdForm.newPassword}
              onChange={handlePwdChange}
              className="input-field"
              autoComplete="new-password"
            />
            {pwdErrors.newPassword && <div className="text-sm text-red-500 mt-1">{pwdErrors.newPassword}</div>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Confirm new password
            </label>
            <input
              name="confirmPassword"
              type="password"
              value={pwdForm.confirmPassword}
              onChange={handlePwdChange}
              className="input-field"
              autoComplete="new-password"
            />
            {pwdErrors.confirmPassword && <div className="text-sm text-red-500 mt-1">{pwdErrors.confirmPassword}</div>}
          </div>

          <div className="flex items-center justify-end gap-3 mt-4">
            <button
              type="button"
              className="px-4 py-2 rounded-lg border dark:border-gray-700"
              onClick={() => setShowChangePassword(false)}
              disabled={pwdLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={pwdLoading}
            >
              {pwdLoading ? 'Saving...' : 'Save password'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Profile;